'use client';

import useWindowDimensions from '@hooks/useMediaQuery';
import { Avatar, Button, LinkStyled } from '@components/common';
import { commonStyles } from '@components/templates/employer/profile/common-styles';
import { cn } from '@utils/cn';
import { fetcherSplit } from '@utils/data-fetching';
import { useTranslation } from 'next-i18next';
import { useState, useEffect } from 'react';

// Interface
interface EmployerRatingsInfiniteProps {
  initialList: ApiSchema<'SimilarWorkplaceCardData'>[];
  totalPages?: number;
  workplaceSlug: string;
  locale: Locale;
}

export const SimilarWorkplacesListInfinite = (props: EmployerRatingsInfiniteProps) => {
  const {
    initialList, totalPages, workplaceSlug, locale,
  } = props;
  const { t } = useTranslation('common');

  // Window dimensions
  const { isMobile } = useWindowDimensions();

  // State
  const [similarWorkplaces, setSimilarWorkplaces] = useState<ApiSchema<'SimilarWorkplaceCardData'>[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(!!totalPages && currentPage < totalPages);
  const [mounted, setMounted] = useState(false);

  // Only set initial list after mount to avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
    if (mounted) {
      const initialItems = isMobile ? initialList.slice(0, 3) : initialList.slice(0, 10);
      setSimilarWorkplaces(initialItems);
    }
  }, [mounted, initialList, isMobile]);

  const loadMore = async () => {
    if (!hasMore || loading) return;

    setLoading(true);

    const limit = isMobile ? 3 : 10;
    const { data } = await fetcherSplit<ApiSchema<'SimilarWorkplaceResult'>>(
      `/v1/workplace/${workplaceSlug}/similar?page=${currentPage + 1}&limit=${limit}`,
      locale,
    );

    if (data?.items?.length) {
      setSimilarWorkplaces((prev) => [
        ...prev,
        ...(data.items as ApiSchema<'SimilarWorkplaceCardData'>[]),
      ]);
    }

    if (data?.page && data?.pages) {
      setCurrentPage(data.page);
      setHasMore(data.page < data.pages);
    }

    setLoading(false);
  };

  // Avoid rendering until mounted to prevent hydration errors
  if (!mounted) return null;

  return (
    <div>
      <h2 className={cn(commonStyles.title, 'mb-4')}>
        {t('company.detail.similar')}
      </h2>
      <div className="space-y-1 text-sm">
        {similarWorkplaces.map((similarWorkplace) => (
          <LinkStyled
            key={similarWorkplace.id}
            styling="none"
            href={`/company-profile/${similarWorkplace.slug}`}
            prefetch={false}
            className="group relative -mx-2 flex items-center space-x-4 rounded px-2 py-1 transition-colors hover:z-1 hover:bg-surface-50"
          >
            <Avatar
              imgPath={similarWorkplace.logoName}
              imgFolder="employer_logo"
              name={similarWorkplace.name}
              size="sm"
              aspectRatio="square-fit"
              rounding="base"
              className="shrink-0"
            />
            <div className="min-w-0 flex-1">
              <span className="block truncate font-bold">
                {similarWorkplace.name}
              </span>
            </div>
          </LinkStyled>
        ))}
      </div>
      {hasMore && (
        <>
          <div className={cn(commonStyles.readMoreGradient, '-mt-7 h-7')} />
          <div className="flex justify-center">
            <Button
              isLoading={loading}
              size="sm"
              color="white"
              rounding="full"
              className={commonStyles.showMoreButton}
              onClick={() => { void loadMore(); }}
            >
              {t('global.label.see_more')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
